import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createPageUrl } from '../utils';
import { useAuth } from '../firebase/AuthContext';
import { base44 } from '../api/base44Client';
import { generateCharacterImage, getProviderStatus } from '../api/hybridImageClient';
import { SURPRISE_ID, pickSurpriseCharacter } from '../components/data/characters';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLanguage } from '../components/LanguageContext';
import CharacterSelector from '../components/photobooth/CharacterSelector';
import CameraCapture from '../components/photobooth/CameraCapture';
import ResultDisplay from '../components/photobooth/ResultDisplay';
import { BASE_PROMPT, CHARACTERS } from '../components/data/characters';

// Fal.ai Seedream 4 API Integration
// Model: fal-ai/bytedance/seedream/v4/edit
// To use: Enable Backend Functions and use fal.subscribe() with FAL_KEY
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  ArrowRight,
  Coins,
  AlertCircle,
  CheckCircle,
  Camera,
  Wand2,
  Calendar,
  Sparkles,
  Confetti,
  Trophy,
  Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';

export default function Photobooth() {
  const { t, language, isRTL } = useLanguage();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const eventPreferenceRef = React.useRef({ didInit: false, hadStoredValue: false });
  const NONE_EVENT_SENTINEL = '__none__';

  const { user: firebaseUser } = useAuth();

  const [step, setStep] = useState(1); // 1: select character, 2: take photo, 3: result, 4: frame composer
  // Mirror of step in a ref so URL-driven useEffects can read the latest step
  // without adding `step` to their deps (which would re-fire them on every step change).
  const stepRef = React.useRef(1);
  React.useEffect(() => { stepRef.current = step; }, [step]);
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  // What "Surprise Me" rolled this run, and what it rolled last time so a second
  // go never lands on the same character twice.
  const [surpriseReveal, setSurpriseReveal] = useState(null);
  const [lastSurpriseId, setLastSurpriseId] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [capturedFile, setCapturedFile] = useState(null);
  const [generatedImage, setGeneratedImage] = useState(null);
  const [finalImage, setFinalImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [generatingProvider, setGeneratingProvider] = useState(null);
  // Default to the 1-credit tier. 'ultra' costs 2 credits and only differs in the
  // `resolution` field, which the server forwards to grok models alone — so
  // whenever a non-grok model serves (which is the normal case), both tiers send
  // an identical request and charging two credits buys the guest nothing.
  const [quality, setQuality] = useState('standard');
  const [detectedFaceCount, setDetectedFaceCount] = useState(1);
  const [activeEventId, setActiveEventId] = useState(null);
  const [providerStatus, setProviderStatus] = useState(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState('');
  // Guests get ZERO credits. The 5 free credits are awarded on registration only.
  // (Previously stored in localStorage — that was abusable: clear localStorage to refill.)
  const { updateMe, saveGeneration, spendCreditsBackend, getAuthToken } = useAuth();
  const user = firebaseUser;
  const credits = firebaseUser ? (firebaseUser.credits ?? 0) : 0;
  const generationCost = quality === 'ultra' ? 2 : 1;
  const isGuest = !firebaseUser;

  // Cleanup any leftover guest credits in localStorage from older versions
  useEffect(() => {
    try { localStorage.removeItem('photobooth_credits'); } catch { /* ignore */ }
  }, []);

  const spendCredits = async (amount) => {
    if (!firebaseUser) return;
    // Always charge via SQLite — it's the authoritative source. AuthContext
    // will mirror the new balance back to Firestore for UI consistency.
    try {
      await spendCreditsBackend(amount, 'Image generation');
    } catch (e) {
      console.warn('Failed to spend credits via backend:', e.message);
      // Fallback: at least update Firestore mirror so UI shows progress
      const newVal = Math.max(0, (firebaseUser.credits ?? 0) - amount);
      try { await updateMe({ credits: newVal }); } catch { /* offline */ }
    }
  };

  const refetchUser = () => { };

  const {
    data: eventsData = { events: [] },
    // isInitialLoading, not isLoading: these queries are `enabled: !!firebaseUser`,
    // and in React Query v4 a disabled query reports isLoading === true forever —
    // which left logged-out visitors staring at "Loading..." instead of the
    // character grid, permanently.
    isInitialLoading: isLoadingEvents,
  } = useQuery({
    queryKey: ['events', firebaseUser?.uid],
    queryFn: async () => {
      const token = await getAuthToken();
      if (!token) return { events: [] };
      const r = await fetch('/api/events', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) return { events: [] };
      const j = await r.json();
      return { events: Array.isArray(j) ? j : (j.events || []) };
    },
    enabled: !!firebaseUser,
  });

  const events = eventsData.events || [];

  // Load active event id from localStorage once
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = 'base44_active_event_id';
    const storedId = window.localStorage.getItem(key);
    if (storedId === '') {
      window.localStorage.removeItem(key);
      eventPreferenceRef.current.hadStoredValue = false;
    } else {
      eventPreferenceRef.current.hadStoredValue = storedId !== null;
      if (storedId && storedId !== NONE_EVENT_SENTINEL) setActiveEventId(storedId);
    }
    eventPreferenceRef.current.didInit = true;
  }, []);

  // Check provider status on component mount
  useEffect(() => {
    const checkProviders = async () => {
      try {
        const status = await getProviderStatus();
        setProviderStatus(status);
        console.log('🔍 Provider status:', status);
      } catch (error) {
        console.error('Failed to check provider status:', error);
      }
    };
    checkProviders();
  }, []);

  // Persist active event id
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!eventPreferenceRef.current.didInit) return;
    window.localStorage.setItem(
      'base44_active_event_id',
      activeEventId ? String(activeEventId) : NONE_EVENT_SENTINEL
    );
  }, [activeEventId]);

  const activeEvent = useMemo(() => {
    if (!events || !events.length || !activeEventId) return null;
    return events.find((ev) => String(ev.id) === String(activeEventId)) || null;
  }, [events, activeEventId]);

  const {
    data: customCharsData = { characters: [] },
    isInitialLoading: isLoadingCustomCharacters,
  } = useQuery({
    queryKey: ['customCharacters', firebaseUser?.uid],
    queryFn: async () => {
      const token = await getAuthToken();
      if (!token) return { characters: [] };
      const r = await fetch('/api/custom-characters', { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
    enabled: !!firebaseUser,
  });

  const customCharacters = useMemo(() => {
    return (customCharsData.characters || []).map((c) => {
      const thumb =
        c.background_image?.data_url ||
        (Array.isArray(c.style_images) && c.style_images[0]?.data_url) ||
        '';
      return {
        id: `custom_${c.id}`,
        name_he: c.name_he || c.name,
        name_en: c.name_en || c.name,
        prompt: c.prompt,
        category: 'custom',
        image_url: thumb,
        image: c.background_image || null,
        isCustom: true,
        customCharacterId: String(c.id),
      };
    });
  }, [customCharsData]);

  useEffect(() => {
    if (!eventPreferenceRef.current.didInit) return;
    if (eventPreferenceRef.current.hadStoredValue) return;
    if (activeEventId) return;
    if (!events || !events.length) return;
    setActiveEventId(String(events[0].id));
    eventPreferenceRef.current.hadStoredValue = true;
  }, [events, activeEventId]);

  const availableCharacters = useMemo(() => {
    if (!activeEvent) {
      // No active event -> show everything
      return [...CHARACTERS, ...customCharacters];
    }

    const standardAllowed = Array.isArray(activeEvent.allowed_characters) ? activeEvent.allowed_characters : [];
    const customAllowed = Array.isArray(activeEvent.custom_character_ids) ? activeEvent.custom_character_ids : [];

    // If the event has absolutely no restrictions configured, fallback to all characters
    if (standardAllowed.length === 0 && customAllowed.length === 0) {
      return [...CHARACTERS, ...customCharacters];
    }

    const allowedSet = new Set(standardAllowed);
    const standardCharacters = CHARACTERS.filter((c) => allowedSet.has(c.id));

    const customAllowedSet = new Set(customAllowed.map(String));
    const customForEvent = customCharacters.filter((c) => 
      customAllowedSet.has(String(c.customCharacterId)) || customAllowedSet.has(String(c.id))
    );

    return [...standardCharacters, ...customForEvent];
  }, [activeEvent, customCharacters]);

  const isInitializing = isLoadingEvents || !eventPreferenceRef.current.didInit;

  const handleActivateEvent = () => {
    if (!events || !events.length) return;
    setActiveEventId(String(events[0].id));
  };

  // Auto-select character from URL parameter (used when coming from Gallery)
  useEffect(() => {
    const characterId = searchParams.get('character');
    if (!characterId) return;

    const character = CHARACTERS.find((c) => c.id === characterId);
    if (character) {
      setSelectedCharacter(character);
      if (stepRef.current === 1) setStep(2);
    }
  }, [searchParams]);

  // Auto-select a CUSTOM character by id (from Dashboard "השתמש" button)
  // Run ONLY ONCE per unique customCharacterId — guard with a ref so subsequent
  // re-renders (which happen often because getAuthToken changes identity) don't
  // reset the step back to 2 after the user clicks Generate.
  const loadedCustomIdRef = React.useRef(null);
  useEffect(() => {
    const customId = searchParams.get('customCharacterId');
    if (!customId || !firebaseUser) return;
    if (loadedCustomIdRef.current === customId) return; // already loaded
    loadedCustomIdRef.current = customId;
    (async () => {
      try {
        const token = await getAuthToken();
        if (!token) return;
        const r = await fetch('/api/custom-characters', { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const { characters } = await r.json();
        const c = (characters || []).find(x => String(x.id) === String(customId));
        if (!c) return;
        setSelectedCharacter({
          id: `custom_${c.id}`,
          name_he: c.name_he || c.name,
          name_en: c.name_en || c.name,
          prompt: c.prompt,
          category: 'custom',
          image: c.background_image || null,
          isCustom: true,
        });
        if (stepRef.current === 1) setStep(2);
      } catch (e) { console.warn('Load custom character failed:', e.message); }
    })();
    // Intentionally NOT including getAuthToken in deps — only react to id + user
    // eslint-disable-next-line
  }, [searchParams, firebaseUser]);

  // Also support raw customPrompt + customName — same guard pattern
  const loadedCustomPromptRef = React.useRef(null);
  useEffect(() => {
    const customPrompt = searchParams.get('customPrompt');
    const customName = searchParams.get('customName');
    if (!customPrompt) return;
    if (loadedCustomPromptRef.current === customPrompt) return;
    loadedCustomPromptRef.current = customPrompt;
    setSelectedCharacter({
      id: 'custom_prompt',
      name_he: customName || 'דמות מותאמת',
      name_en: customName || 'Custom Character',
      prompt: customPrompt,
      category: 'custom',
      isCustom: true,
    });
    if (stepRef.current === 1) setStep(2);
  }, [searchParams]);

  const hasSingleCharacter = activeEvent && availableCharacters.length === 1;

  const handleCharacterSelect = (character) => {
    setSelectedCharacter(character);
    setStep(2);
  };

  const handleCapture = async (capture) => {
    const file = (capture && typeof capture === 'object') ? capture.file : null;
    const preview = (capture && typeof capture === 'object') ? capture.previewUrl : capture;
    setCapturedImage(preview || null);
    setCapturedFile(file || null);

    // Auto-detect face count after capture
    if (file) {
      const count = await detectFaceCount(file);
      setDetectedFaceCount(count);
      console.log(`👥 Auto-detected ${count} face(s)`);
    }
  };

  const handleClearCapture = () => {
    setCapturedImage(null);
    setCapturedFile(null);
    setGeneratedImage(null);
    setFinalImage(null);
  };

  // If an event restricts to a single character, auto-select it and skip to step 2
  useEffect(() => {
    if (hasSingleCharacter && step === 1 && availableCharacters.length === 1) {
      setSelectedCharacter(availableCharacters[0]);
      setStep(2);
    }
  }, [hasSingleCharacter, step, availableCharacters]);

  const progressIntervalRef = React.useRef(null);

  const startProgress = () => {
    setGenerationProgress(0);
    setCurrentStage(language === 'he' ? 'מתחיל...' : 'Starting...');
    const startTime = Date.now();
    // Calibrated for ~35-60s total generation (2048px + enhance)
    const stages = [
      { pct: 8,  label: language === 'he' ? 'מעלה תמונה...'   : 'Uploading image...',    ms: 3000  },
      { pct: 20, label: language === 'he' ? 'מזהה פנים...'     : 'Detecting faces...',    ms: 10000 },
      { pct: 40, label: language === 'he' ? 'מחיל דמות...'     : 'Applying character...', ms: 20000 },
      { pct: 60, label: language === 'he' ? 'משפר איכות...'    : 'Enhancing quality...',  ms: 33000 },
      { pct: 78, label: language === 'he' ? 'מסיים...'         : 'Finishing...',          ms: 45000 },
      { pct: 88, label: language === 'he' ? 'כמעט סיימנו...'   : 'Almost done...',        ms: 58000 },
    ];
    progressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const active = [...stages].reverse().find(s => elapsed >= s.ms);
      if (active) {
        setGenerationProgress(active.pct);
        setCurrentStage(active.label);
      }
    }, 1000);
  };

  const finishProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setGenerationProgress(100);
    setCurrentStage(language === 'he' ? 'הושלם!' : 'Complete!');
  };

  const resetProgress = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    setGenerationProgress(0);
    setCurrentStage('');
  };

  // Detect number of faces using Chrome FaceDetector (works in all Chromium browsers)
  // For unsupported browsers, falls back to 1 — the group prompt still improves results.
  const detectFaceCount = async (imageFile) => {
    try {
      if ('FaceDetector' in window) {
        const imageBitmap = await createImageBitmap(imageFile);
        // fastMode: false → accurate detection (slower by ~200ms but finds all faces)
        const detector = new window.FaceDetector({ fastMode: false, maxDetectedFaces: 20 });
        const faces = await detector.detect(imageBitmap);
        imageBitmap.close();
        const count = faces.length;
        if (count > 0) return count;
      }
    } catch {
      // ignore — fall through to heuristic
    }
    // Heuristic: wide/landscape photos often mean multiple people
    return 1;
  };

  // Build a prompt that explicitly covers ALL people and forbids adding extras
  // Detect non-human transformations (alien, monster, robot, animal, etc.)
  // For these, we must RELAX the "keep exact face/skin" rules so the AI can
  // actually transform the people into the creature — not just dress them up.
  const isCreatureTransformation = (text) => {
    if (!text) return false;
    const lower = text.toLowerCase();
    const en = /\b(alien|monster|zombie|vampire|werewolf|ghost|demon|orc|elf|dwarf|robot|cyborg|android|dragon|mermaid|fairy|angel|skeleton|mummy|witch.*creature|troll|ogre|goblin|gremlin|yeti|sasquatch|bigfoot|minotaur|centaur|cat.*person|dog.*person|animal.*hybrid|na'vi|avatar.*blue)\b/i.test(lower);
    const he = /(חייזר|מפלצת|זומבי|ערפד|רוח|שד|רובוט|דרקון|בת ים|פיה|מלאך|שלד|מומיה|מכשפה|טרול|אוגר|יקטי|חתול אדם|כלב אדם|נצי|אווטאר)/i.test(text);
    return en || he;
  };

  const buildMultiPersonPrompt = (basePrompt, faceCount, isCreature = false) => {
    // For CREATURE transformations: looser rules — keep number of people and
    //   facial expression/structure, but allow skin/eyes/body to transform.
    // For HUMAN-COSTUME transformations: strict identity preservation.
    if (isCreature) {
      const header = [
        `THIS IS A COSTUME / SETTING TRANSFORMATION TASK.`,
        `Take the EXACT photo I gave you and place EVERY person into the creature-themed setting/outfit described below — but KEEP their real human faces.`,
        ``,
        `🚨 RULES — MANDATORY 🚨`,
        ``,
        `1. COUNT: Same number of people in output as in input. If input has 4 people, output has 4 people. Never add or remove.`,
        `2. DO NOT add extra creatures, background figures, friends, or group members. Only transform the existing people.`,
        `⛔ 3. FACE — STRICT: Use the EXACT human face from the original photo for each person. Same eyes, same nose, same mouth, same face shape, same hairstyle, same hair color, same SKIN TONE (do NOT make skin green/blue/grey/alien-colored), same age, same gender, same ethnicity. The face must remain 100% human and recognizable — as if the same person is wearing a costume or standing on an alien set.`,
        `4. ONLY the CLOTHING, EQUIPMENT (helmets, suits, gear), and BACKGROUND/SETTING change to match the creature/sci-fi theme. Think "human actors in a movie about aliens", not "the people turned into aliens".`,
        `5. Each individual stays in the same spatial position as in the original photo.`,
        ``,
        `❌ FORBIDDEN: changing facial features, changing skin color, adding alien skin texture to faces, alien eyes, alien mouths, alien ears, antennae on faces, or any non-human face modifications.`,
        ``,
        `Style: photorealistic, ultra-detailed, sharp, professional movie-quality.`,
        ``,
        `Theme to apply (clothing + setting only — NOT faces):`,
        ``,
      ].join('\n');
      return header + basePrompt;
    }

    // Standard human-costume transformation — strict identity preservation
    const header = [
      `THIS IS A PHOTO EDIT TASK, NOT A NEW IMAGE.`,
      `Take the EXACT photo I gave you and ONLY change the clothing/costume and background to match the character described below.`,
      ``,
      `🚨 IDENTITY PRESERVATION RULES — ABSOLUTELY MANDATORY 🚨`,
      ``,
      `⛔ STOP. Before generating, COUNT the exact number of human faces in the input photo.`,
      `⛔ Your output MUST contain THE EXACT SAME NUMBER OF PEOPLE — not one more, not one less.`,
      `⛔ DO NOT add classmates, friends, students, group members, wizards, soldiers, athletes, or ANY additional people that suggest a "group". Even if the character or scene typically has multiple people, USE ONLY the exact people in the input photo.`,
      `⛔ Common failure: when transforming people into wizards/students/soldiers/dancers, models often add 1-2 extra background members. DO NOT do this. If input has 4 people, output has 4 people. PERIOD.`,
      ``,
      `1. GENDER: If a person in the original is FEMALE, they MUST remain FEMALE in the output. If MALE, they remain MALE. NEVER change anyone's gender.`,
      `2. SKIN TONE: Each individual person KEEPS their own exact skin tone and ethnicity. Do not lighten, darken, or change anyone's race.`,
      `3. AGE: Each person stays the same age — if they are teenage girls in the original, they remain teenage girls (not adults, not men).`,
      `4. FACE: Use the EXACT face from the original photo for each person. Same eyes, same nose, same mouth, same face shape, same hairstyle, same hair color. DO NOT generate new faces.`,
      `5. COUNT (MOST IMPORTANT): Same number of people as input. Do not add. Do not remove. Do not duplicate.`,
      ``,
      `✅ ONLY allowed changes: clothing/uniform/costume, background scene, lighting style, color grading.`,
      `❌ FORBIDDEN: adding ANY new person, changing faces, changing gender, changing skin color, changing ethnicity, removing people, replacing anyone with a different person.`,
      ``,
      `If the character name suggests a group (e.g. "wizard students", "soldiers", "team"), IGNORE the group implication — only the people physically in the input photo exist in the output. No extras.`,
      ``,
      `If the character name suggests a specific gender (e.g. "marine soldier", "ballerina"), IGNORE that gender bias — keep the original people's gender as-is.`,
      ``,
      `Now apply this character transformation to ONLY the existing people in the photo:`,
      ``,
    ].join('\n');
    return header + basePrompt;
  };

  // A plainer wording of the same request, used only as a retry when the
  // preferred model refuses the job on moderation grounds. It asks for exactly
  // the same outcome — same people, same faces, only clothing and background
  // change — without the emphatic "EXACT face / never change gender / never
  // change ethnicity" phrasing that a safety filter can read as an attempt to
  // manipulate someone's identity.
  const buildNeutralPrompt = (basePrompt) => [
    `Edit the attached photo. Keep the people in it as they are and restyle only their clothing and the background to match the description below.`,
    ``,
    `Guidelines:`,
    `- Keep the same number of people as the photo. Do not add anyone, and do not remove anyone.`,
    `- Keep each person recognisable: same face, hair and colouring. Do not substitute anyone.`,
    `- Change only clothing, props, background, lighting and colour grading.`,
    `- Keep faces uncovered and clearly visible. Keep everyone in the same position in the frame.`,
    `- Photorealistic and sharp, framed like a studio portrait from roughly the waist up.`,
    ``,
    `Description to apply:`,
    ``,
  ].join('\n') + basePrompt;

  const handleGenerate = async () => {
    if (!selectedCharacter || !capturedImage || !capturedFile || credits < generationCost) return;

    setError(null);
    setIsGenerating(true);
    setGeneratingProvider(null);
    setStep(3);

    // Start real-time progress tracker
    startProgress();

    try {
      // Deduct credits (Firestore for logged-in users, localStorage for guests)
      await spendCredits(generationCost);

      // Use the face count already detected at capture time (or re-detect as fallback)
      const faceCount = detectedFaceCount > 0 ? detectedFaceCount : await detectFaceCount(capturedFile);
      console.log(`👥 Using face count: ${faceCount}`);

      // "Surprise Me" is a placeholder tile, not a costume — roll the real
      // character now, so a re-run gives a different one. Everything downstream
      // (prompt, name shown on the result, saved history) uses what we rolled.
      let character = selectedCharacter;
      if (selectedCharacter.id === SURPRISE_ID) {
        character = pickSurpriseCharacter(lastSurpriseId);
        setLastSurpriseId(character.id);
        setSurpriseReveal(character);
        console.log(`🎲 Surprise Me rolled: ${character.name_en} (${character.id})`);
      } else {
        setSurpriseReveal(null);
      }

      // Generate image using hybrid service
      const characterLabel = character.name_en || character.name_he || 'the selected character';
      let basePrompt = character.prompt || `${BASE_PROMPT}
Character: ${characterLabel}
Transform into: ${characterLabel}
Body: Appropriate attire and pose for ${characterLabel}
Setting: Appropriate background for ${characterLabel}
Lighting: Professional lighting
Style: Photorealistic, 8k, highly detailed`;

      // For custom characters with a very short prompt — flesh it out so the
      // model has enough to work with. Especially important for non-Latin prompts.
      if (character.isCustom && basePrompt.length < 80) {
        basePrompt = `Subject: ${basePrompt}.
Make it look real and cinematic. Detailed textures, professional lighting.
Setting and props should match the subject description.
Style: photorealistic, 8k, highly detailed.`;
      }

      // Decide whether to treat this as a creature transformation (alien, monster, …)
      // by scanning the prompt + character names. Looser identity rules apply.
      const isCreature = isCreatureTransformation(
        `${character.name_he || ''} ${character.name_en || ''} ${basePrompt}`
      );
      if (isCreature) console.log('🧬 Creature transformation mode — relaxed identity rules');

      const prompt = buildMultiPersonPrompt(basePrompt, faceCount, isCreature);
      const promptNeutral = buildNeutralPrompt(basePrompt);

      console.log('🔄 Starting hybrid image generation...');
      setGeneratingProvider('trying');

      const { imageUrl, provider, raw, degraded } = await generateCharacterImage({
        prompt,
        // Retry wording for the preferred model, used only if it refuses the job.
        promptNeutral,
        imageFile: capturedFile,
        quality,
        faceCount: detectedFaceCount,
        // Match AI image aspect ratio to the event's polaroid orientation
        // so the polaroid frame won't crop people on the sides.
        frameId: activeEvent?.frame_id || null,
        // Generation runs through /api/images/edit so the Pollinations key stays
        // server-side; that endpoint needs the caller's token.
        getAuthToken,
      });

      setGeneratingProvider(provider);

      if (!imageUrl) {
        throw new Error('No image URL returned from generation service');
      }

      console.log(`✅ Image generated successfully using ${provider}!`);
      if (degraded) {
        // A last-resort model produced this. It returns a photo, but not the
        // guest's face — say so rather than letting them find out at the print.
        toast.warning(
          language === 'he'
            ? 'כל מנועי האיכות היו עסוקים, אז התמונה נוצרה במנוע גיבוי — הדמיון לפנים עשוי להיות נמוך. שווה לנסות שוב.'
            : 'All high-quality engines were busy, so a backup engine made this — the face may not match well. Worth trying again.'
        );
      }
      finishProgress();

      setGeneratedImage(imageUrl);

      // Auto-apply the event's frame (Polaroid + caption + logo) if the event has one.
      // The user configured this in Events page → it composes onto every generated photo.
      let composed = imageUrl;
      const eventFrameId = activeEvent?.frame_id;
      if (eventFrameId && (eventFrameId === 'polaroid_portrait' || eventFrameId === 'polaroid_landscape')) {
        try {
          const { composePolaroidImage } = await import('../utils/polaroidLayout');
          // Editor dimensions used when the user designed the layout — must match InteractiveFrameEditor.
          const editorW = eventFrameId === 'polaroid_landscape' ? 600 : 400;
          const editorH = eventFrameId === 'polaroid_landscape' ? 400 : 600;
          composed = await composePolaroidImage({
            baseImageSrc: imageUrl,
            layoutId: eventFrameId,
            frameColor: activeEvent?.frame_color || '#FFFFFF',
            elements: activeEvent?.frame_elements || [],
            editorWidth: editorW,
            editorHeight: editorH,
          });
          console.log('🖼️ Event frame applied automatically');
        } catch (frameErr) {
          console.warn('Frame composition failed (showing unframed):', frameErr.message);
        }
      }
      setFinalImage(composed);

      // Save to user's history — upload the image to persistent storage first
      // (blob: and data: URLs don't survive a page refresh).
      console.log('💾 [history] Starting save flow. user?', !!firebaseUser, 'composed type:',
                   composed?.startsWith('data:') ? 'data-url' :
                   composed?.startsWith('blob:') ? 'blob' : 'http');
      (async () => {
        try {
          if (!firebaseUser) { console.warn('💾 [history] No user, abort'); return; }

          // Decide which URL to persist:
          //   1. Polaroid-composed image (data:) → must upload to our server (it's locally generated)
          //   2. Plain FAL output → use FAL's CDN URL directly (lasts weeks, no server storage needed)
          let persistentUrl = null;

          if (composed?.startsWith('data:')) {
            // Polaroid case — upload the composed image to our server
            const firebaseToken = await getAuthToken();
            if (!firebaseToken) { console.warn('💾 [history] No token, abort'); return; }
            console.log('💾 [history] Uploading polaroid-composed image...');
            const upRes = await fetch('/api/upload-image', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${firebaseToken}` },
              body: JSON.stringify({ dataUrl: composed }),
            });
            const upData = await upRes.json().catch(() => ({}));
            if (!upRes.ok || !upData.url) {
              console.error('💾 [history] Polaroid upload failed:', upRes.status, upData);
              return;
            }
            persistentUrl = upData.url;
            console.log('💾 [history] Polaroid stored at', persistentUrl);
          } else if (raw?.outputUrl) {
            // Use FAL's CDN URL directly — no upload, no server storage cost
            persistentUrl = raw.outputUrl;
            console.log('💾 [history] Using FAL CDN URL directly:', persistentUrl);
          } else if (typeof composed === 'string' && composed.startsWith('http')) {
            persistentUrl = composed;
          } else {
            console.warn('💾 [history] No persistent URL available, skipping save');
            return;
          }

          const saveResult = await saveGeneration({
            character_id: character.id || null,
            character_name: characterLabel,
            image_url: persistentUrl,
            provider,
            quality,
            frame_id: activeEvent?.frame_id || null,
            event_id: activeEventId || null,
            face_count: faceCount,
            credits_spent: generationCost,
          });
          console.log('💾 [history] saveGeneration result:', saveResult);
        } catch (e) {
          console.error('💾 [history] Save failed:', e?.message, e);
        }
      })();

      // Trigger celebration effect
      setShowCelebration(true);
      setTimeout(() => setShowCelebration(false), 3000);

    } catch (err) {
      console.error('Generation error:', err);
      resetProgress();
      // "Try again" is wrong advice for an empty account or a moderated photo —
      // one needs a top-up, the other needs a different photo or character.
      const reason = err?.reason;
      setError(
        reason === 'billing'
          ? (language === 'he'
              ? 'כל מנועי היצירה דחו את הבקשה כרגע (בעיית חשבון או תקלה אצל הספק). נסה שוב בעוד רגע — אם זה חוזר, בדוק את היתרה ב-Pollinations.'
              : 'Every engine rejected the request right now (a provider account or outage issue). Try again shortly — if it persists, check the Pollinations balance.')
          : reason === 'moderation'
            ? (language === 'he'
                ? 'התמונה הזו נחסמה על ידי מסנן התוכן. נסה תמונה אחרת או דמות אחרת.'
                : 'This photo was blocked by the content filter. Try a different photo or character.')
            : (language === 'he'
                ? 'אירעה שגיאה ביצירת התמונה. נסה שוב.'
                : 'Error generating image. Please try again.')
      );
      // Refund the credit
      await spendCredits(-generationCost);
      setStep(2);
    } finally {
      setIsGenerating(false);
      setGeneratingProvider(null);
    }
  };

  const handleTryAnother = () => {
    setSelectedCharacter(null);
    setCapturedImage(null);
    setCapturedFile(null);
    setGeneratedImage(null);
    setFinalImage(null);
    setGeneratingProvider(null);
    setStep(1);
  };

  const getProviderDisplay = () => {
    switch (generatingProvider) {
      case 'trying':
        return language === 'he' ? 'מנסה ספק...' : 'Trying provider...';
      case 'pollinations':
        return '🌻 Pollinations';
      case 'fal':
        return '🤖 FAL.ai';
      default:
        return null;
    }
  };

  const BackArrow = isRTL ? ArrowRight : ArrowLeft;
  const ForwardArrow = isRTL ? ArrowLeft : ArrowRight;

  const steps = [
    { num: 1, title: language === 'he' ? 'בחר דמות' : 'Select Character', icon: Wand2 },
    { num: 2, title: language === 'he' ? 'צלם תמונה' : 'Take Photo', icon: Camera },
    { num: 3, title: language === 'he' ? 'התוצאה' : 'Result', icon: CheckCircle },
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            {step > 1 && step < 3 && (
              <Button
                onClick={() => setStep(step - 1)}
                variant="ghost"
                size="icon"
                className="text-slate-400 hover:text-white"
              >
                <BackArrow className="w-5 h-5" />
              </Button>
            )}
            <h1 className="text-2xl font-bold text-white">{t('photobooth')}</h1>
          </div>

          {/* Credits Badge */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30">
              <Coins className="w-5 h-5 text-amber-400" />
              <span className="font-semibold text-amber-400">{credits}</span>
              <span className="text-amber-400/70 text-sm">{t('credits')}</span>
            </div>

            {/* Provider Status */}
            {providerStatus && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/70 border border-slate-800">
                <div className={`w-2 h-2 rounded-full ${providerStatus.pollinations?.available ? 'bg-green-400' : 'bg-red-400'}`} />
                <span className="text-xs text-slate-300">
                  {providerStatus.pollinations?.available
                    ? (language === 'he' ? '⚡ מחובר' : '⚡ Online')
                    : (language === 'he' ? '❌ מנותק' : '❌ Offline')}
                </span>
              </div>
            )}

            {activeEvent && (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900/70 border border-slate-800">
                  <Calendar className="w-4 h-4 text-violet-400" />
                  <span className="text-xs sm:text-sm text-slate-200 font-medium">
                    {activeEvent.event_name || activeEvent.name || `Event ${activeEvent.id}`}
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-4 mb-10">
          {steps.map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className="flex items-center gap-2">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${step >= s.num
                  ? 'bg-gradient-to-br from-violet-600 to-pink-600'
                  : 'bg-slate-800'
                  }`}>
                  <s.icon className={`w-5 h-5 ${step >= s.num ? 'text-white' : 'text-slate-500'}`} />
                </div>
                <span className={`hidden sm:block text-sm font-medium ${step >= s.num ? 'text-white' : 'text-slate-500'
                  }`}>
                  {s.title}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`w-12 h-0.5 ${step > s.num ? 'bg-violet-600' : 'bg-slate-800'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* No Credits Alert */}
        {credits < 1 && step !== 3 && (
          <Alert className="mb-6 bg-red-500/10 border-red-500/30">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <AlertDescription className="text-red-400 flex items-center justify-between">
              <span>{t('noCredits')}</span>
              <Link to={createPageUrl('Pricing')}>
                <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white">
                  {t('buyCredits')}
                </Button>
              </Link>
            </AlertDescription>
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert className="mb-6 bg-red-500/10 border-red-500/30">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <AlertDescription className="text-red-400">{error}</AlertDescription>
          </Alert>
        )}

        {/* Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Character Selection */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="glass-card rounded-2xl p-6">
                <h2 className="text-xl font-semibold text-white mb-6">{t('selectCharacter')}</h2>
                {isInitializing || isLoadingCustomCharacters ? (
                  <div className="py-12 text-center text-slate-400">
                    <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    {language === 'he' ? 'טוען נתונים...' : 'Loading...'}
                  </div>
                ) : (
                  <CharacterSelector
                    selectedCharacter={selectedCharacter}
                    onSelect={handleCharacterSelect}
                    characters={availableCharacters}
                  />
                )}

                {/* Custom Character Button - Only show if no active event to prevent guest distraction */}
                {!activeEvent && (
                  <div className="mt-6 pt-6 border-t border-slate-700">
                    <Link to={createPageUrl('CustomCharacter')}>
                      <Button className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white h-14 text-lg rounded-xl flex items-center justify-center gap-3">
                        <Wand2 className="w-5 h-5" />
                        {language === 'he' ? 'דמות מותאמת אישית' : 'Custom Character'}
                        <Sparkles className="w-4 h-4" />
                      </Button>
                    </Link>
                    <p className="mt-2 text-sm text-slate-400 text-center">
                      {language === 'he'
                        ? 'צור דמות ייחודית משלך והפוך אותה למציאות'
                        : 'Create your own unique character and bring it to life'
                      }
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Step 2: Camera / Photo */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <div className="grid md:grid-cols-2 gap-6">
                {/* Selected Character Preview */}
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    {language === 'he' ? 'הדמות שנבחרה' : 'Selected Character'}
                  </h3>
                  <div className="relative rounded-xl overflow-hidden">
                    <img
                      src={selectedCharacter?.image_url}
                      alt={language === 'he' ? selectedCharacter?.name_he : selectedCharacter?.name_en}
                      className="w-full aspect-[3/4] object-cover"
                    />
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                      <span className="text-lg font-semibold text-white">
                        {language === 'he' ? selectedCharacter?.name_he : selectedCharacter?.name_en}
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={() => setStep(1)}
                    className="w-full mt-4 bg-slate-700 hover:bg-slate-600 text-white font-medium"
                  >
                    {language === 'he' ? 'שנה דמות' : 'Change Character'}
                  </Button>
                </div>

                {/* Camera */}
                <div className="glass-card rounded-2xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">{t('takePhoto')}</h3>
                  <CameraCapture
                    onCapture={handleCapture}
                    capturedImage={capturedImage}
                    onClear={handleClearCapture}
                  />

                  {/* Quality selection */}
                  <div className="mt-4 flex gap-3">
                    <Button
                      type="button"
                      onClick={() => setQuality('standard')}
                      className={`flex-1 h-11 text-sm font-medium border border-slate-700 ${quality === 'standard'
                        ? 'bg-slate-700 hover:bg-slate-600 text-white'
                        : 'bg-slate-900 hover:bg-slate-800 text-slate-300'
                        }`}
                    >
                      {language === 'he'
                        ? 'איכות רגילה (1 קרדיט)'
                        : 'Standard (1 credit)'}
                    </Button>
                    <Button
                      type="button"
                      onClick={() => setQuality('ultra')}
                      className={`flex-1 h-11 text-sm font-medium border border-violet-700 ${quality === 'ultra'
                        ? 'bg-violet-600 hover:bg-violet-700 text-white'
                        : 'bg-slate-900 hover:bg-slate-800 text-slate-300'
                        }`}
                    >
                      {language === 'he'
                        ? '4K (2 קרדיטים)'
                        : '4K Ultra (2 credits)'}
                    </Button>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    {language === 'he'
                      ? 'איכות רגילה מתאימה לשיתוף מהיר ברשתות ולתצוגה על מסכים רגילים. איכות 4K Ultra מומלצת להדפסה, מסכים גדולים ותוצאות מקסימליות.'
                      : 'Standard quality is perfect for quick sharing and regular screens. 4K Ultra is recommended for printing, large displays, and maximum detail.'}
                  </p>

                  {capturedImage && isGuest && (
                    <Link to="/login" className="block mt-4">
                      <Button className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white h-14 text-lg rounded-xl">
                        <Wand2 className="w-5 h-5 me-2" />
                        {language === 'he'
                          ? 'הירשם בחינם וקבל 5 קרדיטים ליצירה'
                          : 'Sign up free and get 5 credits to generate'}
                      </Button>
                    </Link>
                  )}

                  {capturedImage && !isGuest && credits < generationCost && (
                    <Link to="/pricing" className="block mt-4">
                      <Button className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white h-14 text-lg rounded-xl">
                        <Wand2 className="w-5 h-5 me-2" />
                        {language === 'he'
                          ? `נגמרו לך הקרדיטים — קנה חבילה (חסר ${generationCost - credits})`
                          : `Out of credits — buy a package (need ${generationCost - credits} more)`}
                      </Button>
                    </Link>
                  )}

                  {capturedImage && !isGuest && credits >= generationCost && (
                    <Button
                      onClick={handleGenerate}
                      disabled={isGenerating}
                      className="w-full mt-4 bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-700 hover:to-pink-700 text-white h-14 text-lg rounded-xl"
                    >
                      <Wand2 className="w-5 h-5 me-2" />
                      {language === 'he' ? 'צור קסם!' : 'Create Magic!'}
                      <span className="ms-2 text-sm opacity-75">
                        {language === 'he'
                          ? `(-${generationCost} קרדיטים)`
                          : `(-${generationCost} ${t('credits')})`}
                      </span>
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 3: Result */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.4 }}
            >
              <div className="glass-card rounded-2xl p-6 relative">
                {/* Celebration Overlay */}
                <AnimatePresence>
                  {showCelebration && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm rounded-2xl"
                    >
                      <div className="relative">
                        {/* Confetti Animation */}
                        {[...Array(12)].map((_, i) => (
                          <motion.div
                            key={i}
                            initial={{
                              x: 0,
                              y: 0,
                              scale: 0,
                              rotate: Math.random() * 360
                            }}
                            animate={{
                              x: (Math.random() - 0.5) * 300,
                              y: -Math.random() * 200 - 50,
                              scale: [0, 1, 0.8],
                              rotate: Math.random() * 720 - 360,
                            }}
                            transition={{
                              duration: 2 + Math.random() * 2,
                              ease: "easeOut",
                            }}
                            className="absolute w-3 h-3"
                            style={{
                              left: `${Math.random() * 100}%`,
                              top: `${Math.random() * 100}%`,
                            }}
                          >
                            <div className={`w-full h-full rounded-full ${i % 3 === 0 ? 'bg-electric-blue' :
                              i % 3 === 1 ? 'bg-electric-purple' :
                                'bg-pink-500'
                              }`} />
                          </motion.div>
                        ))}

                        {/* Success Message */}
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.3, duration: 0.5, type: "spring" }}
                          className="relative z-10 flex flex-col items-center gap-4"
                        >
                          <motion.div
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{ duration: 1, repeat: Infinity, ease: "easeInOut" }}
                            className="w-16 h-16 rounded-full bg-gradient-to-br from-electric-blue to-electric-purple flex items-center justify-center shadow-2xl"
                          >
                            <Trophy className="w-8 h-8 text-white" />
                          </motion.div>

                          <motion.div
                            initial={{ y: 20, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ delay: 0.5, duration: 0.5 }}
                            className="text-center"
                          >
                            <h3 className="text-2xl font-bold bg-gradient-to-r from-electric-blue to-electric-purple bg-clip-text text-transparent mb-2">
                              {language === 'he' ? 'מדהים!' : 'Amazing!'}
                            </h3>
                            <p className="text-white text-lg">
                              {language === 'he' ? 'התמונה נוצרה בהצלחה' : 'Image created successfully'}
                            </p>
                          </motion.div>

                          {/* Stars Animation */}
                          <div className="flex gap-2">
                            {[...Array(5)].map((_, i) => (
                              <motion.div
                                key={i}
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: [0, 1, 0], opacity: [0, 1, 0] }}
                                transition={{
                                  duration: 1,
                                  repeat: 2,
                                  delay: i * 0.1,
                                  ease: "easeInOut"
                                }}
                              >
                                <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
                              </motion.div>
                            ))}
                          </div>
                        </motion.div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <ResultDisplay
                  originalImage={capturedImage}
                  generatedImage={finalImage || generatedImage}
                  characterName={language === 'he' ? (surpriseReveal || selectedCharacter)?.name_he : (surpriseReveal || selectedCharacter)?.name_en}
                  onTryAnother={handleTryAnother}
                  isLoading={isGenerating}
                  overlayText={activeEvent?.overlay_text}
                  watermarkText={activeEvent?.watermark_text}
                  logoUrl={activeEvent?.logo_url}
                  onEditFrame={() => setStep(4)}
                  generationProgress={generationProgress}
                  currentStage={currentStage}
                />
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
    </div>
  );
}
